import React, { useState, useEffect } from 'react'

import { getChannelClient } from 'src/utils/messages/singleton'
import { sendPublic } from 'src/utils/messages/publicProtocol'
import { buildCompletedDonationPayload } from 'src/utils/messages/completedDonation'
import I18n, { usd }  from 'src/components/I18n'
import CrowdfundSetup from './CrowdfundSetup'
import PaymentMethods from './PaymentMethods'
import Panel          from './Panel'
import Goal           from './goal/App'
import Matching       from './Matching'
import Finished       from './Finished'
import Fiat           from './fiat/App'
import Crypto         from './crypto/App'
import Wallet         from './wallet/App'
import Deferred       from './deferred/App'
import Launcher       from './external/Launcher'
import Stock          from './external/Stock'
import Daf            from './external/Daf'
import useAbandonedCart from './useAbandonedCart'
import useTribute       from './useTribute'
import usePlugins       from './usePlugins'
import useEmployerMatching from './useEmployerMatching'

import useSimulatedDonation from './useSimulatedDonation'

const App = ({
  paymentMethod, paymentMethods, fiatPaymentMethod, currencies,
  partner, partner_item, partner_items, goal_partner_item,
  goal_threshold, goal, loaded_as_child, showActiveTotals, tipping,
  disable_donation_split, show_input_labels, collect_address, collect_phone,
  matching, stripe, elements, stripeElements, thankyou_info, social, embeddedOn,
  donationsPath, donate_again, rain_down_hearts, postDonationComment, session,
  creditCardProps, blackbaudCheckoutProps, paypalProps, bankProps, walletProps,
  cryptoProps, stockProps, dafProps, deferredProps, removeProfileLink,
  crowdfundSetupUrl, sympathy_card_enabled, abandoned_cart, share_modal,
  employerMatchingProps,
  ...props
}) => {
  const [ walletBalance, setWalletBalance ] = useState(walletProps?.balance)
  const [ donation, setDonation ] = useState(null)
  const [ panel, setPanel ] = useState(paymentMethod)
  // Donate-widget-side abandoned-cart wiring. The hook tracks donor
  // engagement (presets clicked → L1, typed/pasted → L2), announces
  // every transition to the manager (local in mode 1, over the
  // channel in modes 2+3), and announces donation-completed when
  // the donation transitions to truthy. The manager owns all the
  // trigger listeners; this component just declares the inputs.
  useAbandonedCart({
    completed: !!donation,
    enabled: !!abandoned_cart,
    partnerKey: partner.key,
    defaultExternalId: partner_item.external_id,
    locale: props.i18n.locale,
  })

  const i18n = I18n(props.i18n)
  const tribute = useTribute({ i18n, tribute: props.tribute, order: props.order })
  const employerMatching = useEmployerMatching(employerMatchingProps)
  const plugins = usePlugins({
    i18n,
    currency: usd,
    plugins: props.plugins,
    paymentMethod: panel,
    paymentMethods,
    session,
    showInputLabels: show_input_labels,
  })

  const dispatchHeartEvent = () => {
    if (!rain_down_hearts) return
    if (parent == self)
      dispatchEvent(new CustomEvent('rainHearts',
        { detail: props.brand_color }))
    else
      getChannelClient().send('rainHearts', props.brand_color)
  }

  const dispatchCompletedEvent = detail => {
    dispatchEvent(new CustomEvent('donationCompleted', { detail }))

    const sameOrigin = (() => {
      try {
        return location.origin && location.origin == parent.location.origin
      } catch (e) {
        return false
      }
    })()

    if (!sameOrigin && detail.simulated) return

    // Same-origin embedders get the full donation; cross-origin gets the
    // public slice — amounts in the donation's own currency plus `currency`
    // + `currencyType` (see completedDonation.js). `panel` is the active
    // payment method at completion; v6 has already collapsed card / bank /
    // PayPal / Venmo into 'fiat', and crypto maps to crypto. Stock isn't
    // matched by name at all — it reports no amount, so it lands on 'other'.
    //
    // `panel` is read from the closure of the render that set `donation`
    // (this effect's deps are [ donation ] — see below), which is safe
    // because it can't change inside that window: the effect only fires on
    // the render where donation flips truthy, Finished's "donate again"
    // clears donation without touching panel, and crypto/Info.jsx's deferred
    // hand-off is cancelled on unmount. Same invariant as v7's
    // useDonationCompletion, and it's genuinely equivalent now — v6's crypto
    // timeout used to survive unmount, which is what let a stale panel label
    // the payload.
    const data = sameOrigin
      ? detail
      : buildCompletedDonationPayload(detail, panel)

    // Round-trip through JSON to strip Big.js instances, partnerItem
    // methods, and other non-structured-cloneable values that live on
    // the donation state object. v0's stringify dropped them silently;
    // v1's structured-clone postMessage would throw DataCloneError.
    sendPublic(parent, 'donationCompleted', JSON.parse(JSON.stringify(data)))
  }

  useEffect(() => {
    if (donation) {
      dispatchCompletedEvent(donation)
      setTimeout(dispatchHeartEvent, 1)
      window.onbeforeunload = () => {}
    } else {
      window.onbeforeunload = null
    }
  }, [ donation ])

  useSimulatedDonation({ session, setDonation, partnerItem: partner_item })

  useEffect(() => {
    if (donation?.balance != null)
      setWalletBalance(donation.balance)
  }, [ donation?.balance ])

  const commonInputProps = (label, showInputLabels=show_input_labels) => ({
    label,
    requiredLabel: false,
    ...(!showInputLabels && {
      labelClass: 'visually-hidden',
      placeholder: label,
    }),
  })

  return (
    <div>
      {
        Object.keys(paymentMethods).length > 1 &&
        <PaymentMethods
          i18n={i18n}
          paymentMethod={panel}
          setPaymentMethod={setPanel}
          paymentMethods={paymentMethods}
          disabled={!!donation}
          currencies={currencies}
        />
      }
      {
        crowdfundSetupUrl &&
        <CrowdfundSetup crowdfundSetupUrl={crowdfundSetupUrl} />
      }
      {
        goal &&
        <Goal
          i18n={i18n}
          partner={partner}
          partner_item={partner_item}
          partner_items={partner_items}
          goal_partner_item={goal_partner_item}
          loaded_as_child={loaded_as_child}
          goal_threshold={goal_threshold}
          showActiveTotals={showActiveTotals}
          currency={usd}
          donated={!!donation}
          blank_modal_src={props.blank_modal_src}
          widget_instance_token={props.widget_instance_token}
        />
      }
      {
        matching && panel == 'fiat' &&
        <Matching i18n={i18n} currency={usd} matching={matching} />
      }
      <Panel.Collection panel={donation ? 'donated' : panel}>
        <Panel name="wallet" tabpanel>
          <Wallet
            {...props}
            {...walletProps}
            balance={walletBalance}
            i18n={i18n}
            tribute={tribute}
            partner={partner}
            partner_item={partner_item}
            partner_items={partner_items}
            goal_partner_item={goal_partner_item}
            embeddedOn={embeddedOn}
            setDonation={setDonation}
            commonInputProps={commonInputProps}
            donationsPath={donationsPath}
            disable_donation_split={disable_donation_split}
            plugins={plugins}
          />
        </Panel>
        <Panel name="fiat" tabpanel>
          <Fiat
            {...props}
            i18n={i18n}
            tribute={tribute}
            partner={partner}
            partner_item={partner_item}
            partner_items={partner_items}
            goal_partner_item={goal_partner_item}
            embeddedOn={embeddedOn}
            setDonation={setDonation}
            tipping={tipping}
            session={session}
            collect_address={collect_address}
            collect_phone={collect_phone}
            show_input_labels={show_input_labels}
            commonInputProps={commonInputProps}
            panel={panel}
            stripe={stripe}
            elements={elements}
            stripeElements={stripeElements}
            donationsPath={donationsPath}
            currencies={currencies}
            fiatPaymentMethods={paymentMethods.fiat}
            fiatPaymentMethod={fiatPaymentMethod}
            disable_donation_split={disable_donation_split}
            plugins={plugins}
            employerMatching={employerMatching}
            creditCardProps={creditCardProps}
            blackbaudCheckoutProps={blackbaudCheckoutProps}
            bankProps={bankProps}
            paypalProps={paypalProps}
            sympathy_card_enabled={sympathy_card_enabled}
          />
        </Panel>
        <Panel name="stock" tabpanel>
          <Launcher
            {...props}
            {...stockProps}
            Button={Stock}
            paymentMethod="stock"
            paymentPartnerName="Donate Stock"
            i18n={i18n}
            partner={partner}
            partner_item={partner_item}
            partner_items={partner_items}
            goal_partner_item={goal_partner_item}
            embeddedOn={embeddedOn}
            session={session}
            donationsPath={donationsPath}
            setDonation={setDonation}
            commonInputProps={commonInputProps}
            plugins={plugins}
            footer={
              <small>
                {i18n.t('donate_stock.heading')}<br/>
                <a
                  href="https://drive.google.com/file/d/1Dh0tApOQhDlzud2F7H8UZMed9Zhp2GVL/view"
                  target="_blank"
                >
                  {i18n.t('donate_stock.learn_more')}
                </a>
              </small>
            }
          />
        </Panel>
        <Panel name="daf" tabpanel>
          <Launcher
            {...props}
            {...dafProps}
            Button={Daf}
            paymentMethod="daf"
            paymentPartnerName="Chariot"
            i18n={i18n}
            partner={partner}
            partner_item={partner_item}
            partner_items={partner_items.filter(item => item.beneficiary.valid_ein)}
            goal_partner_item={goal_partner_item}
            embeddedOn={embeddedOn}
            session={session}
            donationsPath={donationsPath}
            setDonation={setDonation}
            commonInputProps={commonInputProps}
            plugins={plugins}
            footer={
              <small>
                {i18n.t('chariot.heading')}<br/>
                <a href="https://help.givechariot.com/dafpay/editor" target="_blank">
                  {i18n.t('chariot.learn_more')}
                </a>
              </small>
            }
          />
        </Panel>
        <Panel name="crypto" tabpanel>
          <Crypto
            {...props}
            {...cryptoProps}
            i18n={i18n}
            tribute={tribute}
            partner={partner}
            partner_item={partner_item}
            partner_items={partner_items}
            goal_partner_item={goal_partner_item}
            embeddedOn={embeddedOn}
            setDonation={setDonation}
            session={session}
            donationsPath={donationsPath}
            disable_donation_split={disable_donation_split}
            show_input_labels={show_input_labels}
            commonInputProps={commonInputProps}
            collect_address={collect_address}
            collect_phone={collect_phone}
            panel={panel}
            elements={elements}
            plugins={plugins}
          />
        </Panel>
        <Panel name="deferred" tabpanel>
          <Deferred
            {...props}
            {...deferredProps}
            i18n={i18n}
            tribute={tribute}
            partner={partner}
            partner_item={partner_item}
            partner_items={partner_items}
            goal_partner_item={goal_partner_item}
            embeddedOn={embeddedOn}
            setDonation={setDonation}
            session={session}
            collect_address={collect_address}
            collect_phone={collect_phone}
            show_input_labels={show_input_labels}
            commonInputProps={commonInputProps}
            donationsPath={donationsPath}
            currencies={currencies}
            disable_donation_split={disable_donation_split}
            panel={panel}
            elements={elements}
            plugins={plugins}
          />
        </Panel>
        <Panel name="donated">
          <Finished
            i18n={i18n}
            removeProfileLink={removeProfileLink}
            social={social}
            share_modal={share_modal}
            thankYouInfo={thankyou_info}
            donationsPath={donationsPath}
            donation={donation}
            setDonation={setDonation}
            postDonationComment={postDonationComment}
            plugins={plugins}
            paymentMethod={panel}
            donate_again={donate_again}
            employerMatching={employerMatching}
          />
        </Panel>
      </Panel.Collection>
    </div>
  )
}

export default App
